import { createAsyncThunk } from '@reduxjs/toolkit';
import {
    postStartPlanning,
    getPlanningSession,
    postSubmitAnswers,
    postSubmitFeedback,
    putEditPlan,
    postFinalizePlan,
    postRetryPlanning,
    postOffloadPlanning
} from 'src/api/planning';
import {
    StartPlanningRequest,
    SubmitAnswersRequest,
    SubmitFeedbackRequest,
    EditPlanRequest,
    FinalizePlanRequest,
    PlanningSessionData
} from 'src/types/planning';

export const SLICE_NAME = 'planning';

const ACTION_TYPES = {
    START: `${SLICE_NAME}/start`,
    FETCH: `${SLICE_NAME}/fetch`,
    ANSWER: `${SLICE_NAME}/answer`,
    FEEDBACK: `${SLICE_NAME}/feedback`,
    EDIT: `${SLICE_NAME}/edit`,
    FINALIZE: `${SLICE_NAME}/finalize`,
    OFFLOAD: `${SLICE_NAME}/offload`,
    RETRY: `${SLICE_NAME}/retry`,
    DRAFT: `${SLICE_NAME}/draft`
};

export const startPlanningSession = createAsyncThunk(
    ACTION_TYPES.START,
    async (params: StartPlanningRequest, { rejectWithValue }) => {
        try {
            const response = await postStartPlanning(params);
            if (response?.status === 'ok') {
                return response.data as {
                    sessionId?: string;
                    questId?: string;
                    breakdownJobId?: string;
                };
            }
            return rejectWithValue('Failed to start planning session');
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const fetchPlanningSession = createAsyncThunk(
    ACTION_TYPES.FETCH,
    async (sessionId: string, { rejectWithValue }) => {
        try {
            const response = await getPlanningSession(sessionId);
            if (response?.status === 'ok') {
                return response.data as PlanningSessionData;
            }
            return rejectWithValue('Failed to fetch planning session');
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const submitAnswers = createAsyncThunk(
    ACTION_TYPES.ANSWER,
    async (
        {
            sessionId,
            params
        }: { sessionId: string; params: SubmitAnswersRequest },
        { rejectWithValue }
    ) => {
        try {
            const response = await postSubmitAnswers(sessionId, params);
            if (response?.status === 'ok') {
                return response.data as PlanningSessionData;
            }
            return rejectWithValue('Failed to submit answers');
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const submitFeedback = createAsyncThunk(
    ACTION_TYPES.FEEDBACK,
    async (
        {
            sessionId,
            params
        }: { sessionId: string; params: SubmitFeedbackRequest },
        { rejectWithValue }
    ) => {
        try {
            const response = await postSubmitFeedback(sessionId, params);
            if (response?.status === 'ok') {
                return response.data as PlanningSessionData;
            }
            return rejectWithValue('Failed to submit feedback');
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const editPlan = createAsyncThunk(
    ACTION_TYPES.EDIT,
    async (
        { sessionId, params }: { sessionId: string; params: EditPlanRequest },
        { rejectWithValue }
    ) => {
        try {
            const response = await putEditPlan(sessionId, params);
            if (response?.status === 'ok') {
                return response.data as PlanningSessionData;
            }
            return rejectWithValue('Failed to edit plan');
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const finalizePlan = createAsyncThunk(
    ACTION_TYPES.FINALIZE,
    async (
        {
            sessionId,
            params
        }: { sessionId: string; params: FinalizePlanRequest },
        { rejectWithValue }
    ) => {
        try {
            const response = await postFinalizePlan(sessionId, params);
            if (response?.status === 'ok') {
                return response.data as {
                    questId: string;
                    breakdownJobId?: string;
                };
            }
            return rejectWithValue('Failed to finalize plan');
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const retryPlanningSession = createAsyncThunk(
    ACTION_TYPES.RETRY,
    async (sessionId: string, { rejectWithValue }) => {
        try {
            const response = await postRetryPlanning(sessionId);
            if (response?.status === 'ok') {
                return response.data as PlanningSessionData;
            }
            return rejectWithValue('Failed to retry planning session');
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const offloadPlanningSession = createAsyncThunk(
    ACTION_TYPES.OFFLOAD,
    async (
        {
            sessionId,
            params
        }: {
            sessionId: string;
            params: {
                priority: number;
                status: number;
                alwaysHumanReview?: boolean;
            };
        },
        { rejectWithValue }
    ) => {
        try {
            const response = await postOffloadPlanning(sessionId, params);
            if (response?.status === 'ok') {
                return true;
            }
            return rejectWithValue('Failed to offload planning session');
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);
